import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Box, ChevronLeft, LoaderCircle, Plus, Search, Wrench, X } from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useSharedQuote } from '../../context/QuoteContext';
import { InventoryItemImage } from '../../components/ui/InventoryItemImage';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ReadOnlyBanner } from '../../components/ui/ReadOnlyBanner';
import { CasegoodsInventoryItem, ResidentialItemType } from '../../types/admin';
import { QtyControls } from '../../components/quotes/QtyControls';
import { applyAddEquipment } from '../../utils/quoteInventory';
import { isZeroVolumeMaterial } from '../../lib/inventoryPricing';

// ── Category helpers ──────────────────────────────────────────────────────────

const CATEGORY_ORDER: Record<string, number> = {
  customer_item: 0,
  packing_item: 1,
  equipment: 2,
};

const CATEGORY_LABEL: Record<string, string> = {
  customer_item: 'Customer Item',
  packing_item: 'Packing Item',
  equipment: 'Equipment',
};

const CATEGORY_CLASSES: Record<string, string> = {
  customer_item: 'bg-plum/10 text-plum',
  packing_item: 'bg-amber-50 text-amber-600',
  equipment: 'bg-blue-50 text-blue-600',
};

function CategoryChip({ category }: { category?: ResidentialItemType }) {
  if (!category) return null;
  return (
    <span className={`shrink-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${CATEGORY_CLASSES[category] ?? 'bg-mist text-slate'}`}>
      {CATEGORY_LABEL[category] ?? category}
    </span>
  );
}

// ── Add Items Sheet ───────────────────────────────────────────────────────────

interface AddItemsSheetProps {
  current: CasegoodsInventoryItem[];
  onAdd: (itemId: string, itemName: string) => void;
  onSetQty: (itemId: string, qty: number) => void;
  onClose: () => void;
}

function AddItemsSheet({ current, onAdd, onSetQty, onClose }: AddItemsSheetProps) {
  const { orgItems: allItems, orgItemsLoading: loading, orgItemsError } = useSharedQuote();
  const [search, setSearch] = useState('');

  const casegoods = allItems
    // Zero-volume supplies (tape, wrap, …) are added from Materials instead.
    .filter(i => i.type === 'Casegoods' && (i.quoteType ?? 'commercial') === 'commercial' && !isZeroVolumeMaterial(i))
    .sort((a, b) => {
      const aOrder = CATEGORY_ORDER[a.residentialType ?? 'customer_item'] ?? 0;
      const bOrder = CATEGORY_ORDER[b.residentialType ?? 'customer_item'] ?? 0;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name);
    });

  const filtered = search.trim()
    ? casegoods.filter(i => i.name.toLowerCase().includes(search.trim().toLowerCase()))
    : casegoods;

  const currentMap = new Map(current.map(c => [c.itemId, c.quantity]));

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl flex flex-col max-h-[85vh] md:rounded-2xl md:max-w-lg md:w-full md:max-h-[80vh]">

        {/* Header */}
        <div className="px-4 pt-5 pb-3 flex-shrink-0">
          <div className="w-10 h-1 bg-stone rounded-full mx-auto mb-4 md:hidden" />
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-h3 text-midnight">Add Items</h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                         hover:text-midnight hover:bg-mist transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
              <Search size={14} className="text-slate" />
            </div>
            <input
              type="search"
              placeholder="Search items…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-stone bg-mist
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>
        </div>

        {/* Scrollable list */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {loading && (
            <div className="flex justify-center py-10">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {!loading && orgItemsError && (
            <div className="flex items-start gap-2 px-4 py-3 mb-3 rounded-xl bg-rose/10 border border-rose/30">
              <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
              <p className="text-small text-rose">Couldn&apos;t load inventory: {orgItemsError}</p>
            </div>
          )}

          {!loading && !orgItemsError && filtered.length === 0 && (
            <div className="flex flex-col items-center py-12 gap-2 text-center">
              <p className="text-small text-slate">
                No casegoods items found{search ? ` for "${search}"` : ''}.
              </p>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <ul className="flex flex-col gap-2">
              {filtered.map(item => {
                const qty = currentMap.get(item.id);
                const added = qty !== undefined;

                return (
                  <li
                    key={item.id}
                    className={`flex gap-3 border rounded-xl px-3 py-2.5
                      ${added ? 'bg-plum/5 border-plum/20' : 'bg-white border-stone'}`}
                  >
                    <InventoryItemImage
                      item={item}
                      alt={item.name}
                      wrapperClassName="shrink-0 pt-0.5"
                      className="w-10 h-10 rounded-lg object-cover border border-stone"
                      fallback={(
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center
                          ${added ? 'bg-plum/10' : 'bg-mist'}`}>
                          <Box size={16} className={added ? 'text-plum' : 'text-slate'} />
                        </div>
                      )}
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-small text-midnight truncate font-medium flex items-center gap-1">
                            {item.name}
                            {item.isAccessorial && (
                              <Wrench size={13} className="shrink-0 text-plum" />
                            )}
                          </p>
                          <CategoryChip category={item.residentialType} />
                        </div>

                        {added ? (
                          <QtyControls
                            quantity={qty!}
                            onIncrement={() => onSetQty(item.id, qty! + 1)}
                            onDecrement={() => onSetQty(item.id, qty! - 1)}
                            onDirectEdit={n => onSetQty(item.id, n)}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => onAdd(item.id, item.name)}
                            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg
                                       bg-plum text-white hover:bg-rose active:opacity-80
                                       transition-colors duration-100"
                            aria-label={`Add ${item.name}`}
                          >
                            <Plus size={16} />
                          </button>
                        )}
                      </div>
                      {(item.description || item.notes) && (
                        <div className="mt-1.5 space-y-0.5">
                          {item.description && (
                            <p className="text-[11px] text-slate leading-tight">{item.description}</p>
                          )}
                          {item.notes && (
                            <p className="text-[11px] text-slate/70 leading-tight italic">{item.notes}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Done */}
        <div className="px-4 pt-2 pb-6 flex-shrink-0 border-t border-stone">
          <button
            type="button"
            onClick={onClose}
            className="w-full h-12 rounded-xl bg-plum text-white text-[15px] font-semibold
                       hover:bg-rose active:opacity-80 transition-colors duration-150"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function CasegoodsInventoryPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const { quote, loading, updateQuote, orgItems } = useSharedQuote();

  const [initialized, setInitialized] = useState(false);
  const [inventory, setInventory] = useState<CasegoodsInventoryItem[]>([]);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Initialize local inventory from quote once on load.
  useEffect(() => {
    if (quote && !initialized) {
      setInventory(quote.casegoodsInventory ?? []);
      setInitialized(true);
    }
  }, [quote, initialized]);

  // Auto-save with 400ms debounce whenever inventory changes.
  // quote.quoteStatus is read here (not inside a useCallback) so it's always current.
  useEffect(() => {
    if (!initialized || quote?.quoteStatus !== 'draft') return;
    const timer = setTimeout(() => {
      setSaving(true);
      setSaveError('');
      updateQuote({ casegoodsInventory: inventory })
        .then(() => setSaving(false))
        .catch(err => {
          setSaveError(err instanceof Error ? err.message : 'Auto-save failed');
          setSaving(false);
        });
    }, 400);
    return () => clearTimeout(timer);
  }, [inventory, initialized, quote?.quoteStatus, updateQuote]);

  /** Update qty for an item, applying add-equipment deltas. */
  function setQty(itemId: string, newQty: number) {
    setInventory(prev => {
      const existing = prev.find(i => i.itemId === itemId);
      const oldQty = existing?.quantity ?? 0;

      let updated: CasegoodsInventoryItem[];
      if (newQty <= 0) {
        updated = prev.filter(i => i.itemId !== itemId);
      } else {
        updated = prev.map(i => i.itemId === itemId ? { ...i, quantity: newQty } : i);
      }
      return applyAddEquipment(updated, itemId, oldQty, newQty, orgItems);
    });
  }

  /** Add a new item at qty 1, then apply add-equipment for qty delta of +1. */
  function handleAdd(itemId: string, itemName: string) {
    setInventory(prev => {
      if (prev.some(i => i.itemId === itemId)) return prev;
      const orgItem = orgItems.find(i => i.id === itemId);
      const withItem = [...prev, {
        itemId,
        itemName,
        quantity: 1,
        itemCategory: orgItem?.residentialType ?? 'customer_item',
      }];
      return applyAddEquipment(withItem, itemId, 0, 1, orgItems);
    });
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <p className="text-small text-rose font-semibold">Quote not found</p>
      </div>
    );
  }

  // Build a lookup map for Casegoods org items (for images and type filtering)
  const casegoodsOrgMap = new Map(
    orgItems.filter(i => i.type === 'Casegoods').map(i => [i.id, i]),
  );

  // Only show Casegoods items, sorted by category then alphabetically
  // (matches the add-modal sort: customer_item → packing_item → equipment, then A-Z)
  const displayedInventory = inventory
    .filter(item => casegoodsOrgMap.has(item.itemId))
    .sort((a, b) => {
      const aCat = a.itemCategory ?? casegoodsOrgMap.get(a.itemId)?.residentialType ?? 'customer_item';
      const bCat = b.itemCategory ?? casegoodsOrgMap.get(b.itemId)?.residentialType ?? 'customer_item';
      const aOrder = CATEGORY_ORDER[aCat] ?? 0;
      const bOrder = CATEGORY_ORDER[bCat] ?? 0;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.itemName.localeCompare(b.itemName);
    });

  return (
    <div className="px-4 pt-6 pb-10 md:max-w-3xl md:mx-auto">
      {/* Back — hidden on md+ (shell left panel provides navigation) */}
      <button
        type="button"
        onClick={() => navigate(`/moves/${quoteId}`)}
        className="md:hidden flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        {quote.customerCompanyName}
      </button>

      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-h1 text-midnight">Casegoods Inventory</h1>
        <div className="flex items-center gap-2">
          {saving && <LoaderCircle size={16} className="text-slate animate-spin shrink-0" />}
          {quote.quoteStatus === 'draft' && (
            <button
              type="button"
              onClick={() => setShowAddSheet(true)}
              className="flex items-center gap-1.5 h-9 px-3 rounded-xl bg-plum text-white
                         text-[13px] font-semibold
                         hover:bg-rose active:opacity-80 transition-colors duration-150"
            >
              <Plus size={15} />
              Add
            </button>
          )}
        </div>
      </div>

      {quote.quoteStatus !== 'draft' && <ReadOnlyBanner status={quote.quoteStatus} />}

      {saveError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{saveError}</p>
        </div>
      )}

      <div className={quote.quoteStatus !== 'draft' ? 'pointer-events-none opacity-60 select-none' : ''}>
        {displayedInventory.length === 0 ? (
          <div className="flex flex-col items-center py-20 gap-4 text-center">
            <div className="w-16 h-16 rounded-full bg-mist flex items-center justify-center">
              <Box size={28} className="text-slate" />
            </div>
            <div>
              <p className="text-small font-semibold text-midnight mb-1">No items added yet</p>
              <p className="text-[13px] text-slate leading-snug">
                Tap <span className="font-semibold text-plum">Add</span> to select casegoods items.
              </p>
            </div>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {displayedInventory.map(item => {
              const orgItem = casegoodsOrgMap.get(item.itemId);
              return (
                <li
                  key={item.itemId}
                  className="flex gap-3 bg-white border border-stone rounded-xl px-4 py-3
                             shadow-brand-sm"
                >
                  <InventoryItemImage
                    item={orgItem ?? { id: item.itemId }}
                    alt={item.itemName}
                    wrapperClassName="shrink-0 pt-0.5"
                    className="w-10 h-10 rounded-lg object-cover border border-stone"
                    fallback={(
                      <div className="w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
                        <Box size={16} className="text-plum" />
                      </div>
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-small font-medium text-midnight truncate flex items-center gap-1">
                          {item.itemName}
                          {orgItem?.isAccessorial && (
                            <Wrench size={13} className="shrink-0 text-plum" />
                          )}
                        </p>
                        <CategoryChip category={item.itemCategory ?? orgItem?.residentialType} />
                      </div>
                      <QtyControls
                        quantity={item.quantity}
                        onIncrement={() => setQty(item.itemId, item.quantity + 1)}
                        onDecrement={() => setQty(item.itemId, item.quantity - 1)}
                        onDirectEdit={n => setQty(item.itemId, n)}
                      />
                    </div>
                    {(orgItem?.description || orgItem?.notes) && (
                      <div className="mt-1.5 space-y-0.5">
                        {orgItem.description && (
                          <p className="text-[11px] text-slate leading-tight">{orgItem.description}</p>
                        )}
                        {orgItem.notes && (
                          <p className="text-[11px] text-slate/70 leading-tight italic">{orgItem.notes}</p>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showAddSheet && orgId && (
        <AddItemsSheet
          current={inventory}
          onAdd={handleAdd}
          onSetQty={(itemId, qty) => setQty(itemId, qty)}
          onClose={() => setShowAddSheet(false)}
        />
      )}
    </div>
  );
}
